/**
 * Data hooks for the Payments Sandbox feature.
 *
 * Wraps the six endpoints under `/api/v1/admin/payments/sandbox`:
 *   - GET  /mp-webhooks        (paginated)
 *   - GET  /inter-webhooks     (paginated)
 *   - GET  /payouts            (paginated + status filter)
 *   - GET  /ledger             (paginated + recipientType/entryType/status)
 *   - POST /mp-test            (trigger)
 *   - POST /inter-test-payout  (trigger)
 *
 * Ergonomics mirror `features/error-logs/hooks.ts` and `features/couriers/hooks.ts`:
 * a query-key factory object, `{ data, error }`-style throw-on-error (here the
 * helper throws directly), mutations invalidate the relevant query keys on
 * success and emit a `sonner` toast on success/error. Only the transport
 * function differs — see `./api.ts` for why a typed fetch helper is used
 * instead of the generated `api` client.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isApiError } from '@favelafood/shared/api/errors';
import { toast } from '@favelafood/shared/lib/toast';
import { adminGet, sandboxGet, sandboxPost } from './api';
import type {
  InterHealthResponse,
  InterRealPayoutRequest,
  InterRealPayoutResult,
  InterTestPayoutRequest,
  InterTestPayoutResult,
  InterWebhookView,
  LedgerEntryType,
  LedgerEntryStatus,
  LedgerEntryView,
  MpTestRequest,
  MpTestResult,
  MpWebhookView,
  PageResponse,
  PixOutStatus,
  PixOutView,
  RecipientType,
} from './types';

export interface LedgerFilters {
  recipientType?: RecipientType | undefined;
  entryType?: LedgerEntryType | undefined;
  status?: LedgerEntryStatus | undefined;
}

/** Filters for the "Pagamentos futuros" (upcoming credits) tab (EP-5, DESIGN §6.2). */
export interface UpcomingFilters {
  recipientType?: RecipientType | undefined;
  recipientId?: string | undefined;
  entryType?: LedgerEntryType | undefined;
  scheduledFrom?: string | undefined;
  scheduledTo?: string | undefined;
}

/** Filters for the "Pagamentos concluídos" (completed payouts) tab (EP-5, DESIGN §6.2). */
export interface CompletedFilters {
  recipientType?: RecipientType | undefined;
  recipientId?: string | undefined;
  status?: PixOutStatus | undefined;
  executedFrom?: string | undefined;
  executedTo?: string | undefined;
}

/** TanStack Query key factory — keeps cache invalidations consistent. */
export const paymentsSandboxKeys = {
  all: ['payments-sandbox'] as const,
  mpWebhooks: (page: number, size: number) =>
    ['payments-sandbox', 'mp-webhooks', page, size] as const,
  interWebhooks: (page: number, size: number) =>
    ['payments-sandbox', 'inter-webhooks', page, size] as const,
  payouts: (page: number, size: number, status?: PixOutStatus) =>
    ['payments-sandbox', 'payouts', page, size, status ?? null] as const,
  ledger: (page: number, size: number, filters: LedgerFilters) =>
    ['payments-sandbox', 'ledger', page, size, filters] as const,
  upcoming: (page: number, size: number, filters: UpcomingFilters) =>
    ['payments-sandbox', 'upcoming', page, size, filters] as const,
  completed: (page: number, size: number, filters: CompletedFilters) =>
    ['payments-sandbox', 'completed', page, size, filters] as const,
};

export function useMpWebhooks(
  page: number,
  size: number,
): UseQueryResult<PageResponse<MpWebhookView>, Error> {
  return useQuery<PageResponse<MpWebhookView>, Error>({
    queryKey: paymentsSandboxKeys.mpWebhooks(page, size),
    queryFn: () =>
      sandboxGet<PageResponse<MpWebhookView>>('/mp-webhooks', { page, size }),
  });
}

export function useInterWebhooks(
  page: number,
  size: number,
): UseQueryResult<PageResponse<InterWebhookView>, Error> {
  return useQuery<PageResponse<InterWebhookView>, Error>({
    queryKey: paymentsSandboxKeys.interWebhooks(page, size),
    queryFn: () =>
      sandboxGet<PageResponse<InterWebhookView>>('/inter-webhooks', { page, size }),
  });
}

export function usePayouts(
  page: number,
  size: number,
  status?: PixOutStatus,
): UseQueryResult<PageResponse<PixOutView>, Error> {
  return useQuery<PageResponse<PixOutView>, Error>({
    queryKey: paymentsSandboxKeys.payouts(page, size, status),
    queryFn: () =>
      sandboxGet<PageResponse<PixOutView>>('/payouts', { page, size, status }),
  });
}

export function useLedger(
  page: number,
  size: number,
  filters: LedgerFilters,
): UseQueryResult<PageResponse<LedgerEntryView>, Error> {
  return useQuery<PageResponse<LedgerEntryView>, Error>({
    queryKey: paymentsSandboxKeys.ledger(page, size, filters),
    queryFn: () =>
      sandboxGet<PageResponse<LedgerEntryView>>('/ledger', {
        page,
        size,
        recipientType: filters.recipientType,
        entryType: filters.entryType,
        status: filters.status,
      }),
  });
}

/**
 * Upcoming credits — the "Pagamentos futuros" read-only tab (EP-5).
 * Queries `GET /ledger/upcoming` (PENDING/SCHEDULED payable credits).
 */
export function useUpcoming(
  page: number,
  size: number,
  filters: UpcomingFilters,
): UseQueryResult<PageResponse<LedgerEntryView>, Error> {
  return useQuery<PageResponse<LedgerEntryView>, Error>({
    queryKey: paymentsSandboxKeys.upcoming(page, size, filters),
    queryFn: () =>
      sandboxGet<PageResponse<LedgerEntryView>>('/ledger/upcoming', {
        page,
        size,
        recipientType: filters.recipientType,
        recipientId: filters.recipientId,
        entryType: filters.entryType,
        scheduledFrom: filters.scheduledFrom,
        scheduledTo: filters.scheduledTo,
      }),
  });
}

/**
 * Completed payouts — the "Pagamentos concluídos" read-only tab (EP-5).
 * Queries `GET /payouts/completed` (executed PIX-OUT, default status EXECUTED).
 */
export function useCompleted(
  page: number,
  size: number,
  filters: CompletedFilters,
): UseQueryResult<PageResponse<PixOutView>, Error> {
  return useQuery<PageResponse<PixOutView>, Error>({
    queryKey: paymentsSandboxKeys.completed(page, size, filters),
    queryFn: () =>
      sandboxGet<PageResponse<PixOutView>>('/payouts/completed', {
        page,
        size,
        recipientType: filters.recipientType,
        recipientId: filters.recipientId,
        status: filters.status,
        executedFrom: filters.executedFrom,
        executedTo: filters.executedTo,
      }),
  });
}

/**
 * Folds a thrown error into a toast. SANDBOX_DISABLED / SANDBOX_REAL_PROVIDER
 * are also surfaced inline by `SandboxGuardNotice`, but a toast keeps parity
 * with the rest of the panel's mutation feedback.
 */
const toastError = (err: unknown, fallback: string): void => {
  if (isApiError(err)) toast.fromApiError(err, fallback);
  else toast.error(fallback);
};

export interface UseRunMpTestResult {
  run: (request: MpTestRequest) => Promise<MpTestResult>;
  isPending: boolean;
  data: MpTestResult | undefined;
  error: Error | null;
  reset: () => void;
}

export function useRunMpTest(): UseRunMpTestResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<MpTestResult, Error, MpTestRequest>({
    mutationFn: (request) =>
      sandboxPost<MpTestResult, MpTestRequest>('/mp-test', request),
    onSuccess: () => {
      toast.success('Checkout sandbox executado');
      void queryClient.invalidateQueries({ queryKey: paymentsSandboxKeys.all });
    },
    onError: (err) => toastError(err, 'Falha ao rodar o checkout sandbox'),
  });

  return {
    run: (request) => mutation.mutateAsync(request),
    isPending: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export interface UseInterHealthCheckResult {
  /** Manually triggers the read-only Inter connectivity probe. */
  run: () => Promise<InterHealthResponse>;
  isPending: boolean;
  data: InterHealthResponse | undefined;
  error: Error | null;
  reset: () => void;
}

/**
 * Read-only Banco Inter connectivity probe (EP-6, degrau 1). A manual mutation
 * (no auto-fetch) that GETs `/api/v1/admin/inter/health-check` and exposes the
 * `InterHealthResponse`. It moves NO money — it never fires a payout.
 */
export function useInterHealthCheck(): UseInterHealthCheckResult {
  const mutation = useMutation<InterHealthResponse, Error, void>({
    mutationFn: () => adminGet<InterHealthResponse>('/inter/health-check'),
    onError: (err) => toastError(err, 'Falha ao testar a conexão com o Inter'),
  });

  return {
    run: () => mutation.mutateAsync(),
    isPending: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export interface UseRunInterRealPayoutResult {
  run: (request: InterRealPayoutRequest) => Promise<InterRealPayoutResult>;
  isPending: boolean;
  data: InterRealPayoutResult | undefined;
  error: Error | null;
  reset: () => void;
}

/**
 * Fires the Inter REAL PIX-OUT trigger (EP-6, degrau 2) — POSTs
 * `/inter-real-payout`. Mirrors `useRunInterTestPayout` but drives the REAL
 * provider (behind four backend gates + UI double-confirmation). Invalidates the
 * whole sandbox cache so the payouts table refreshes; toasts in pt-BR.
 */
export function useRunInterRealPayout(): UseRunInterRealPayoutResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<InterRealPayoutResult, Error, InterRealPayoutRequest>({
    mutationFn: (request) =>
      sandboxPost<InterRealPayoutResult, InterRealPayoutRequest>(
        '/inter-real-payout',
        request,
      ),
    onSuccess: () => {
      toast.success('PIX-OUT Inter REAL disparado');
      void queryClient.invalidateQueries({ queryKey: paymentsSandboxKeys.all });
    },
    onError: (err) => toastError(err, 'Falha ao disparar PIX-OUT Inter REAL'),
  });

  return {
    run: (request) => mutation.mutateAsync(request),
    isPending: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export interface UseRunInterTestPayoutResult {
  run: (request: InterTestPayoutRequest) => Promise<InterTestPayoutResult>;
  isPending: boolean;
  data: InterTestPayoutResult | undefined;
  error: Error | null;
  reset: () => void;
}

export function useRunInterTestPayout(): UseRunInterTestPayoutResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<InterTestPayoutResult, Error, InterTestPayoutRequest>({
    mutationFn: (request) =>
      sandboxPost<InterTestPayoutResult, InterTestPayoutRequest>(
        '/inter-test-payout',
        request,
      ),
    onSuccess: () => {
      toast.success('PIX-OUT de teste disparado');
      void queryClient.invalidateQueries({ queryKey: paymentsSandboxKeys.all });
    },
    onError: (err) => toastError(err, 'Falha ao disparar o PIX-OUT de teste'),
  });

  return {
    run: (request) => mutation.mutateAsync(request),
    isPending: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}
